import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { Lock, NotebookPen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useOpenNoteForBooking } from '@/hooks/use-sessions';
import type { BookingSessionNoteSummary } from '@/types/sessions';
import { EMPTY_DOC } from './noteMarks';
import { NoteStatusChip } from './shared';

interface SessionNoteCardProps {
  bookingId: string | number;
  summary: BookingSessionNoteSummary | null | undefined;
}

/**
 * "Sitzungsnotiz" card on booking details (psychotherapy practices only; the
 * API returns no summary otherwise). The author can open or start the note;
 * everyone else only sees whether one exists and its status.
 */
export function SessionNoteCard({ bookingId, summary }: SessionNoteCardProps) {
  const { t } = useTranslation('sessions');
  const [, setLocation] = useLocation();
  const openNote = useOpenNoteForBooking();

  if (!summary) return null;

  const hasNote = summary.status !== null;
  const canOpen = summary.mine && summary.id;
  const canStart = !hasNote && summary.canWrite;

  const start = () =>
    openNote.mutate(
      { bookingId, content: EMPTY_DOC },
      { onSuccess: (note) => setLocation(`/admin/sessions/notes/${note.id}`) },
    );

  return (
    <section
      aria-labelledby="session-note-card"
      className="flex flex-col gap-3 rounded-2xl border-2 border-brand-200 bg-gradient-to-b from-brand-50 to-surface p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-500">
            <NotebookPen className="h-5 w-5 text-white" aria-hidden="true" />
          </span>
          <h3 id="session-note-card" className="m-0 font-display text-lg font-semibold text-ink">{t('booking.cardTitle')}</h3>
        </div>
        {hasNote && <NoteStatusChip status={summary.status} />}
      </div>

      {!hasNote && <p className="m-0 text-sm text-soft">{t('booking.none')}</p>}
      {hasNote && !summary.mine && <p className="m-0 text-sm text-soft">{t('booking.someoneElse')}</p>}
      {!hasNote && !summary.canWrite && <p className="m-0 text-xs text-soft">{t('booking.notYours')}</p>}

      <div className="flex flex-wrap items-center gap-3">
        {canOpen && <Button onClick={() => setLocation(`/admin/sessions/notes/${summary.id}`)}>{t('booking.open')}</Button>}
        {canStart && <Button loading={openNote.isPending} onClick={start}>{t('booking.write')}</Button>}
        {(canOpen || canStart) && (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700">
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
            {t('common.onlyYou')}
          </span>
        )}
      </div>
    </section>
  );
}
